import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { Box, Typography } from "@mui/material";
import Breadcrumbs from "../../../components/BreadCrumbs";
import ApplicantReportTabs from "./ApplicantReportTabs";
import ShiftsAttendanceTable from "./ShiftsAttendanceTable";
import messages from "../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import ApplicantReportModal from "./ApplicantReportModal/ApplicantReportModal";
import TrainingCheckinModal from "./TrainingCheckinModal";
import TrainingAttendanceTable from "./TrainingAttendanceTable";
import {
  getApplicantstTimeSheetRequests,
  getApplicantstTimeSheetResponse,
} from "../../../store/ApplicantsTimeSheet/actions";
import {
  getTrainingWorkingSlotsRequest,
  getTrainingWorkingSlotsResponse,
} from "../../../store/TrainingWorkingSlots/actions";
import { UIButton } from "../../../components/ui";
import DeleteApplicantReportModal from "./DeleteApplicantReportModal/DeleteApplicantReportModal";
import DeleteTrainingSlotModal from "./DeleteTrainingSlotModal/DeleteTrainingSlotModal";
import { isAdmin, isSuperAdmin, isVisitor, isSupervisorManger } from "../../../utils/Permissions";
import { useAdminModulePermission } from "../../../utils/adminPortalHelpers";
import { adminPermissionModules } from "../../../utils/Constants";
import { getPermissionRequest } from "../../../store/Auth/actions";
import store from "../../../store";
import "./TimeSheet.scss";

const ApplicantReport = () => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const { timeSheet, shared } = messages[lang];
  const history = useHistory();

  const [selectedOption, setSelectedOption] = useState(null);
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [openTrainingModal, setOpenTrainingModal] = useState(false);
  const [openDeleteTrainingModal, setOpenDeleteTrainingModal] = useState(false);
  const [selectedTrainingItem, setSelectedTrainingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedTrainingToDelete, setSelectedTrainingToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState("shifts");
  const trainingList = useSelector((state) => state.trainingWorkingSlots.list);
  const list = useSelector((state) => state.applicantsTimeSheetRequest.list);
  const meta = useSelector((state) => state.applicantsTimeSheetRequest.meta);
  const isLoading = useSelector((state) => state.loader);
  const [page, setPage] = useState(1);

  const { eventid, userid } = useParams();

  let location = useLocation();
  const { id } = store.getState().auth.login.role;

  useEffect(() => {
    if (!isSuperAdmin() && !isAdmin() && !isSupervisorManger()) {
      dispatch(getPermissionRequest({ id }));
    }
  }, [lang]);

  const { canAdd, canEdit, canDelete } = useAdminModulePermission(
    adminPermissionModules.timesheet,
  );

  let editDeleteActions = [];

  if (isVisitor()) {
    if (canEdit) {
      editDeleteActions.push(shared.edit);
    }
    if (canDelete) {
      editDeleteActions.push(shared.delete);
    }
  } else if (isSuperAdmin()) {
    editDeleteActions = [shared.edit, shared.delete];
  }

  const clickHandler = (option, item) => {
    setOpenStatusModal(true);
    setSelectedOption(option);
    setSelectedItem(item);
  };

  const handleAddSlotClick = () => {
    if (activeTab === "shifts") {
      clickHandler("Add");
    } else {
      setSelectedTrainingItem(null);
      setOpenTrainingModal(true);
    }
  };

  const handleTrainingModalClose = () => {
    setOpenTrainingModal(false);
    setSelectedTrainingItem(null);
  };
  const handleClose = (status) => {
    setOpenStatusModal(status);
    setSelectedOption(null);
    setSelectedItem(null);
  };

  const handleTrainingDelete = (row) => {
    setSelectedTrainingToDelete(row);
    setOpenDeleteTrainingModal(true);
  };

  const handleTrainingDeleteModalClose = () => {
    setOpenDeleteTrainingModal(false);
    setSelectedTrainingToDelete(null);
  };

  const handleTrainingEdit = (row) => {
    setSelectedTrainingItem(row);
    setOpenTrainingModal(true);
  };

  useEffect(() => {
    if (activeTab !== "shifts") return;

    const params = {
      page,
      items: 10,
      event_id: eventid,
      user_id: userid,
      order_by_check_in_at: "desc",
    };
    if (location?.state?.isDaily) {
      // Removed currently to get all days records in applicant record
      // params.check_in_day = location?.state?.filter_date;
    } else {
      params.check_in_month_in_a_date = location?.state?.filter_date;
    }
    dispatch(getApplicantstTimeSheetRequests(params));
  }, [activeTab, page, lang, eventid, userid, location?.state?.isDaily, location?.state?.filter_date, dispatch]);

  useEffect(() => {
    if (activeTab !== "trainings") return;

    dispatch(
      getTrainingWorkingSlotsRequest({
        page: -1,
        event_id: eventid,
        user_id: userid,
        locale: lang,
      })
    );
  }, [activeTab, eventid, userid, lang, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(
        getApplicantstTimeSheetResponse({
          list: [],
          meta: {},
        })
      );
      dispatch(
        getTrainingWorkingSlotsResponse({
          list: [],
          meta: {},
        })
      );
    };
  }, [dispatch]);

  const shiftsContent = (
    <div className="d-flex flex-column justify-content-between">
      <ShiftsAttendanceTable
        list={list}
        meta={meta}
        isLoading={isLoading}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        timeSheet={timeSheet}
        shared={shared}
        lang={lang}
        editDeleteActions={editDeleteActions}
        onEditOrDelete={clickHandler}
      />
    </div>
  );

  const userName =
    lang === "ar"
      ? location?.state?.user_name?.full_name_ar
      : location?.state?.user_name?.full_name_en;

  const trainingsContent = (
    <div className="d-flex flex-column justify-content-between">
      <TrainingAttendanceTable
        list={trainingList}
        lang={lang}
        editDeleteActions={editDeleteActions}
        onEdit={handleTrainingEdit}
        onDelete={handleTrainingDelete}
      />
    </div>
  );

  const canAddSlot = (isVisitor() && canAdd) || isSuperAdmin();

  return (
    <div className="work-slots-list-container applicant-report-page">
      <div className="page-title-margin">
        <Breadcrumbs
          list={[
            {
              path: ROUTE_PATHS.timesheet,
              label: timeSheet.listTimeSheetBreadcrumb,
            },
            {
              path: "",
              label: `${
                timeSheet[
                  location?.state?.isDaily ? "dailyReport" : "monthlyReport"
                ].name
              } - ${location?.state?.event_name}`,
              isClicked: true,
              onclick: () =>
                history.push(
                  ROUTE_PATHS[
                    location?.state?.isDaily ? "dailyReport" : "monthlyReport"
                  ].replace(":eventid", eventid),
                  { event_name: location?.state?.event_name }
                ),
            },
            {
              path: "",
              label:
                lang === "ar"
                  ? location?.state?.user_name?.full_name_ar
                  : location?.state?.user_name?.full_name_en,
            },
          ]}
          BreadCrumbHasBackIcon={false}
        />
        <Box className="d-flex justify-content-between align-items-center">
          <Typography variant="h5">
            {timeSheet.applicantReport.name}
          </Typography>
          {canAddSlot && (
            <UIButton
              appearance="gradient"
              type="button"
              startIcon={<AddIcon fontSize="small" />}
              onClick={handleAddSlotClick}
            >
              {timeSheet.addStatus}
            </UIButton>
          )}
        </Box>
      </div>

      <ApplicantReportTabs
        timeSheet={timeSheet}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        shiftsContent={shiftsContent}
        trainingsContent={trainingsContent}
      />

      {selectedOption === "Delete" || selectedOption === "حذف" ? (
        <DeleteApplicantReportModal
          open={openStatusModal}
          selectedItem={selectedItem}
          eventId={eventid}
          userId={userid}
          handleClose={handleClose}
          selectedOption={selectedOption}
          isDaily={location?.state?.isDaily}
          dateParam={location?.state?.filter_date}
        />
      ) : (
        <ApplicantReportModal
          handleClose={handleClose}
          open={openStatusModal}
          selectedOption={selectedOption}
          selectedItem={selectedItem}
          isDaily={location?.state?.isDaily}
          dateParam={location?.state?.filter_date}
        />
      )}

      <TrainingCheckinModal
        open={openTrainingModal}
        handleClose={handleTrainingModalClose}
        userName={userName || "-"}
        selectedItem={selectedTrainingItem}
        eventId={eventid}
        userId={userid}
      />

      <DeleteTrainingSlotModal
        open={openDeleteTrainingModal}
        selectedItem={selectedTrainingToDelete}
        eventId={eventid}
        userId={userid}
        handleClose={handleTrainingDeleteModalClose}
      />
    </div>
  );
};

export default ApplicantReport;
